import { sumBy, groupBy } from 'lodash';

import { assertValue, cnxCompare } from '@libs/utils';
import type {
  Money,
  CancellationPolicy,
  TaxesAndFee,
  HotelBookableAndLockableOption,
} from '@libs/trip-planner-interfaces';

import type {
  PriceBreakdownForRoom,
  PriceBreakdownModalData,
  StayPriceBreakdownData,
} from './models';

export function getPriceBreakdownModalData(
  stay: StayPriceBreakdownData
): PriceBreakdownModalData {
  const allTaxesAndFees = getAllTaxesAndFees(stay);
  const taxesAndFeesSummary = getTaxesAndFeesSummary(allTaxesAndFees);
  const cancellationPoliciesSummary =
    getCancellationPoliciesSummarySorted(stay);
  const priceAtLocation = getPriceAtLocation(allTaxesAndFees);
  const priceExcludingTaxes = getPriceExcludingTaxes(
    stay.price,
    allTaxesAndFees
  );
  const forRooms = getSummaryForRooms(stay);

  return {
    summary: {
      priceBreakdown: {
        taxesAndFees: taxesAndFeesSummary,
        priceAtLocation,
        priceExcludingTaxes,
        totalPrice: stay.price,
      },
      cancellationPolicies: cancellationPoliciesSummary,
    },
    forRooms,
  };
}

function getAllTaxesAndFees(stay: StayPriceBreakdownData): TaxesAndFee[] {
  return stay.rooms.flatMap((room) => room.taxesAndFees);
}

export function getTaxesAndFeesSummary(
  allTaxesAndFees: TaxesAndFee[]
): TaxesAndFee[] {
  const groupedByType = groupBy(allTaxesAndFees, (tax) => tax.type);

  return Object.entries(groupedByType).map(([type, groupedTaxes]) => {
    const firstGroupedTax = groupedTaxes[0];
    assertValue(
      firstGroupedTax,
      `cancellationPoliciesUtils.ts > getTaxesAndFeesSummary > firstGroupedTaxes`
    );

    return {
      amount: {
        amount: sumBy(groupedTaxes, (tax) => tax.amount.amount),
        currency: firstGroupedTax.amount.currency,
        currencySymbol: firstGroupedTax.amount.currencySymbol,
      },
      type,
      isIncludedInPrice: firstGroupedTax.isIncludedInPrice,
    };
  });
}

function getAllCancellationPolicies(
  stay: StayPriceBreakdownData | HotelBookableAndLockableOption
): CancellationPolicy[] {
  return stay.rooms.flatMap((room) => room.cancellationPolicies);
}

function getCancellationPoliciesSummary(
  allCancellationPolicies: CancellationPolicy[]
): CancellationPolicy[] {
  const groupedByFromDate = groupBy(
    allCancellationPolicies,
    (policy) => policy.fromDate
  );

  return Object.entries(groupedByFromDate)
    .map(([fromDate, groupedPolicies]) => {
      const firstGroupedPolicy = groupedPolicies[0];
      assertValue(
        firstGroupedPolicy,
        `cancellationPoliciesUtils.ts > getCancellationPoliciesSummary > firstGroupedPolicy`
      );

      return {
        fromDate,
        fromDateIso: firstGroupedPolicy.fromDateIso,
        amount: {
          amount: sumBy(groupedPolicies, (policy) => policy.amount.amount),
          currency: firstGroupedPolicy.amount.currency,
          currencySymbol: firstGroupedPolicy.amount.currencySymbol,
        },
      };
    })
    .sort(cnxCompare);
}

function getCancellationPoliciesSummarySorted(
  stay: StayPriceBreakdownData | HotelBookableAndLockableOption
): CancellationPolicy[] {
  const allCancellationPolicies = getAllCancellationPolicies(stay);

  return getCancellationPoliciesSummary(allCancellationPolicies);
}

function getPriceAtLocation(taxesAndFees: TaxesAndFee[]): Money | undefined {
  const totalTaxesNotIncluded = taxesAndFees.reduce(
    (total: Money, tax: TaxesAndFee) => {
      if (!tax.isIncludedInPrice) {
        total.amount += tax.amount.amount;
        total.currency = tax.amount.currency;
        total.currencySymbol = tax.amount.currencySymbol;
      }

      return total;
    },
    { amount: 0, currency: '', currencySymbol: '' }
  );

  if (
    !totalTaxesNotIncluded.amount ||
    !totalTaxesNotIncluded.currency ||
    !totalTaxesNotIncluded.currencySymbol
  ) {
    return;
  }

  return totalTaxesNotIncluded;
}

function getPriceExcludingTaxes(
  totalPrice: Money,
  taxesAndFees: TaxesAndFee[]
): Money | undefined {
  const totalTaxesIncludedInPrice = taxesAndFees.reduce(
    (total, tax) => (total += tax.isIncludedInPrice ? tax.amount.amount : 0),
    0
  );

  return totalTaxesIncludedInPrice > 0
    ? {
        amount: totalPrice.amount - totalTaxesIncludedInPrice,
        currency: totalPrice.currency,
        currencySymbol: totalPrice.currencySymbol,
      }
    : undefined;
}

function getSummaryForRooms(
  stay: StayPriceBreakdownData
): PriceBreakdownForRoom[] {
  return stay.rooms.map((room) => {
    const priceAtLocation = getPriceAtLocation(room.taxesAndFees);

    const priceExcludingTaxes = getPriceExcludingTaxes(
      room.price,
      room.taxesAndFees
    );

    return {
      roomName: room.name,
      roomSummary: {
        priceBreakdown: {
          priceAtLocation,
          priceExcludingTaxes,
          taxesAndFees: room.taxesAndFees,
          totalPrice: room.price,
        },
        cancellationPolicies: room.cancellationPolicies,
      },
    };
  });
}
